import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUp, Package, Search, MapPin } from "lucide-react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import apis from "../../utils/apis";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import Header from "../../components/Header";
import { useSelector, useDispatch } from "react-redux";
import ProductCard from "../../components/ProductCard";
import { buildCdnURL } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import { DEFAULT_CATEGORY_ID } from "../../utils/constants";
import NoResultsPrompt from "../../components/NoResultsPrompt";
import PullToRefresh from "../../components/PullToRefresh";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";

const HomePage = () => {
  const [searchedProducts, setSeardchedProducts] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.userInfo);
  const martId = user?.defaultAddress?.nearby?.mart?._id;

  const { callApi: getCategories } = useApi();
  const { callApi: searchProducts } = useApi();
  const { callApi: getProducts } = useApi();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Store initial navigation state - this should not change during component lifecycle
  // Check if user navigated here via browser back/forward button (POP) or clicking link (PUSH)
  const isBackNavigationRef = useRef(navigationType === 'POP');
  const isBackNavigation = isBackNavigationRef.current;

  // Navigation state tracked on mount
  useEffect(() => {
    // Component mounted
  }, []);

  // On direct visits, reset queries to start fresh from page 1
  useEffect(() => {
    if (!isBackNavigation) {
      queryClient.resetQueries({ queryKey: ['products', martId, DEFAULT_CATEGORY_ID] });
      queryClient.resetQueries({ queryKey: ['categories', DEFAULT_CATEGORY_ID] });
    }
  }, []); // Only run once on mount

  // Fetch categories with React Query
  const { data: categories, isLoading: categoriesLoading, refetch: refetchCategories } = useQuery({
    queryKey: ['categories', DEFAULT_CATEGORY_ID],
    queryFn: async () => {
      const res = await getCategories({
        api: apis.listCategory,
        query: { parentCategory: DEFAULT_CATEGORY_ID },
      });
      return res;
    },
    enabled: true,
    staleTime: isBackNavigation ? Infinity : 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // Don't refetch - we reset the query on direct visits
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Infinite query for products
  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingProducts,
    isRefetching: isRefetchingProducts,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['products', martId, DEFAULT_CATEGORY_ID],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getProducts({
        api: apis.getProductByCategory,
        param: `${martId}/list/v3/?categoryId=${DEFAULT_CATEGORY_ID}&page=${pageParam}`,
      });
      return {
        products: res?.products || [],
        currentPage: pageParam,
        totalPages: res?.totalPages || 1,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    enabled: !!martId,
    initialPageParam: 1,
    staleTime: isBackNavigation ? Infinity : 0,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false, // Don't refetch - we reset the query on direct visits
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // DEBUG: Log products query config and state
  useEffect(() => {
    console.log('📦 Products Query Config:', {
      enabled: !!martId,
      staleTime: isBackNavigation ? Infinity : 0,
      refetchOnMount: isBackNavigation ? false : 'always',
      isBackNavigation,
      martId,
      queryKey: ['products', martId, DEFAULT_CATEGORY_ID],
    });
    console.log('📦 Products Query State:', {
      isLoading: isLoadingProducts,
      isRefetching: isRefetchingProducts,
      pagesLoaded: productsData?.pages?.length,
      hasNextPage,
      isFetchingNextPage,
    });
  }, [isLoadingProducts, isRefetchingProducts, productsData?.pages?.length]);

  // Flatten products from all pages
  const data = productsData?.pages?.flatMap(page => page.products) || [];
  const totalPages = productsData?.pages?.[0]?.totalPages || 1;
  const currentPage = productsData?.pages?.length || 1;

  // Debounced search term for infinite query
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search input
  useEffect(() => {
    const trimmedQuery = searchedProducts?.trim();
    
    if (!trimmedQuery) {
      setDebouncedSearchTerm("");
      setIsSearching(false);
      // Scroll to top when search is cleared
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSearching(true);
    const searchTimeout = setTimeout(() => {
      setDebouncedSearchTerm(trimmedQuery);
      setIsSearching(false);
      // Scroll to top when search is executed
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [searchedProducts]);

  // Infinite query for search results
  const {
    data: searchResultsData,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
    isFetchingNextPage: isFetchingNextSearchPage,
    isLoading: isLoadingSearch,
    isRefetching: isRefetchingSearch,
  } = useInfiniteQuery({
    queryKey: ['searchProducts', martId, DEFAULT_CATEGORY_ID, debouncedSearchTerm],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await searchProducts({
        api: apis.getProductByCategory,
        param: `${martId}/list/v3/?categoryId=${DEFAULT_CATEGORY_ID}&page=${pageParam}&search=${debouncedSearchTerm}`,
      });
      return {
        products: res?.products || [],
        totalPages: res?.totalPages || 1,
        currentPage: pageParam,
      };
    },
    enabled: !!debouncedSearchTerm && !!martId,
    initialPageParam: 1,
    staleTime: 0, // Always fetch fresh search results
    gcTime: 2 * 60 * 1000, // Keep search results for 2 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  const searchResults = searchResultsData?.pages.flatMap(page => page.products) || [];

  // Clear location state after component mounts to ensure future navigations are treated as direct visits
  // No longer needed since we're using navigationType instead
  useEffect(() => {
    // Clean up logic if needed
  }, []); // Empty deps - only run once on mount

  // Infinite scroll for regular products
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const sentinelRef = useInfiniteScroll(handleLoadMore, hasNextPage, isFetchingNextPage);

  // Infinite scroll for search results
  const handleLoadMoreSearch = useCallback(() => {
    if (hasNextSearchPage && !isFetchingNextSearchPage) {
      fetchNextSearchPage();
    }
  }, [hasNextSearchPage, isFetchingNextSearchPage, fetchNextSearchPage]);

  const searchSentinelRef = useInfiniteScroll(handleLoadMoreSearch, hasNextSearchPage, isFetchingNextSearchPage);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await queryClient.resetQueries({ queryKey: ['products', martId, DEFAULT_CATEGORY_ID] });
  }, [queryClient, martId]);

  const handleCategorySelect = (categoryId) => {
    navigate(`/products/v2/${categoryId}`);
  };

  // Function to get a random pastel color for categories without images
  const getRandomPastelColor = (id) => {
    // Use the last character of the ID to determine the color
    const colors = [
      "bg-blue-100 dark:bg-blue-900",
      "bg-green-100 dark:bg-green-900",
      "bg-yellow-100 dark:bg-yellow-900",
      "bg-red-100 dark:bg-red-900",
      "bg-purple-100 dark:bg-purple-900",
      "bg-pink-100 dark:bg-pink-900",
      "bg-indigo-100 dark:bg-indigo-900",
      "bg-teal-100 dark:bg-teal-900",
    ];

    const lastChar = id.slice(-1);
    const index = Number.parseInt(lastChar, 16) % colors.length;
    return colors[index];
  };

  return (
    <div className="font-sans min-h-screen">
      <Header
        searchQuery={searchedProducts}
        setSearchQuery={setSeardchedProducts}
      />

      <PullToRefresh containerRef={contentRef} onRefresh={handleRefresh}>
        <main className="p-4" ref={contentRef}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Show search results if searching or search results exist */}
            {debouncedSearchTerm || isSearching ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {(isSearching || isLoadingSearch || (isRefetchingSearch && searchResults?.length === 0)) && searchResults?.length === 0 ? (
                  // Product skeleton loaders while searching
                  Array.from({ length: 10 }).map((_, index) => (
                    <ProductCard key={`loading-search-initial-${index}`} loading={true} />
                  ))
                ) : searchResults?.length > 0 ? (
                  <>
                    {/* Display search results */}
                    {searchResults.map((product) => (
                      <ProductCard
                        key={product?.stockEntries?.[0]?._id || product?._id}
                        data={product}
                      />
                    ))}
                    
                    {/* Show skeleton loaders at bottom when loading more search results */}
                    {isFetchingNextSearchPage && (
                      <>
                        {Array.from({ length: 5 }).map((_, index) => (
                          <ProductCard key={`loading-search-${index}`} loading={true} />
                        ))}
                      </>
                    )}

                    {/* Sentinel element for infinite scroll on search results */}
                    {hasNextSearchPage && !isFetchingNextSearchPage && (
                      <div ref={searchSentinelRef} className="col-span-full h-4" />
                    )}
                  </>
                ) : (
                  // No results found
                  <div className="col-span-full flex flex-col items-center justify-center py-12 px-4">
                    <Search className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" />
                    <p className="text-center text-lg font-medium text-gray-600 dark:text-gray-300">
                      {t("products.noProductsFound")}
                    </p>
                    <p className="text-center text-sm mt-2 text-gray-500 dark:text-gray-400">
                      {t("products.tryAdjustingSearch")}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Show categories when not searching
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {categoriesLoading
                  ? // Category skeleton loaders
                    Array.from({ length: 8 }).map((_, index) => (
                      <div
                        key={index}
                        className="section rounded-xl shadow-md overflow-hidden h-48 animate-pulse"
                      >
                        <div className="h-32 bg-gray-300 dark:bg-backround-200"></div>
                        <div className="p-3">
                          <div className="h-4 bg-gray-300 dark:bg-backround-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))
                  : categories?.map((category) => (
                      <motion.div
                        key={category._id}
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                        className="section rounded-xl shadow-md overflow-hidden cursor-pointer transition-all hover:shadow-lg"
                        onClick={() => handleCategorySelect(category._id)}
                      >
                        {category.images?.[0] ? (
                          <div className="h-36 overflow-hidden relative">
                            <img
                              src={
                                buildCdnURL(category.images[0]) ||
                                "/assets/placeholder.webp"
                              }
                              alt={category.name}
                              className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                          </div>
                        ) : (
                          <div
                            className={`h-36 flex items-center justify-center ${getRandomPastelColor(
                              category._id
                            )}`}
                          >
                            <Package className="w-16 h-16 text-gray-700 dark:text-gray-300 opacity-80" />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-800 dark:text-gray-200 capitalize text-center">
                            {category.name}
                          </h3>
                        </div>
                      </motion.div>
                    ))}
              </div>
            )}
          </motion.div>
          {!searchedProducts ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4"
            >
              {/* Show skeleton loaders on initial load or refetch on direct visit */}
              {(isLoadingProducts || (isRefetchingProducts && !isBackNavigation))
                ? Array.from({ length: 10 }).map((_, index) => (
                    <ProductCard key={`loading-initial-${index}`} loading={true} />
                  ))
                : data?.length > 0
                ? data.map((product, idx) => (
                    <ProductCard
                      key={product?.variants?.[0]?._id || product?.stockEntries?.[0]?._id || `${product?.name}-${idx}` }
                      data={product}
                    />
                  ))
                : !data?.length &&
                  !isLoadingProducts && (
                    <div className="col-span-full">
                      <NoResultsPrompt searchText={searchedProducts} />
                    </div>
                  )}

              {/* Show skeleton loaders at bottom when loading more products */}
              {isFetchingNextPage && (
                <>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <ProductCard key={`loading-${index}`} loading={true} />
                  ))}
                </>
              )}

              {/* Sentinel element for infinite scroll */}
              {data?.length > 0 && hasNextPage && !isFetchingNextPage && (
                <div ref={sentinelRef} className="col-span-full h-10" />
              )}
            </motion.div>
          ) : (
            <></>
          )}
        </main>
      </PullToRefresh>
    </div>
  );
};

export default HomePage;
